import {
  EXTENSIONS_MARKETPLACE_PARSE_SERVER_URL,
  EXTENSIONS_MARKETPLACE_PARSE_SERVER_APP_ID,
  EXTENSIONS_MARKETPLACE_PARSE_SERVER_SITE_ID,
} from '@/env'
import {
  initParse,
  getReadyForSalePlugins,
  getUserInstalledApps,
  getSiteInstalledApps,
  findDeveloperByEmail,
  getMyPluginsInDevelopment,
  getRolesList,
} from '@/utils/parse'

const initialPublisher = {
  name: '',
  id: '',
  parse_server_url: '',
  parse_server_app_id: '',
  parse_server_site_id: '',
}

const store = {
  namespaced: true,
  state: {
    activePublisher: initialPublisher,
    publishers: [],
    allSiteApps: [],
    allUserApps: [],
    publisherAppMap: [],
    userInstalledApps: [],
    siteInstalledApps: [],
    siteAppsForMoreBar: [], // Apps formatted for MoreAppBar
    SDKRoles: [],
  },
  mutations: {
    /**
     * Mutation to set the active publisher.
     * @param {Object} publisher - The active publisher object.
     */
    setActivePublisher(state, publisher) {
      state.activePublisher = publisher
    },
    /**
     * Mutation to set the list of publishers.
     * @param {Array} publishers - The list of publishers.
     */
    setPublisherList(state, publishers) {
      state.publishers = publishers
    },
    /**
     * Mutation to set all apps.
     * @param {Object} param - The parameter object containing all apps information.
     */
    setAllApps(state, param) {
      const { allSiteApps, allUserApps, publisherAppMap } = param
      state.allSiteApps = allSiteApps
      state.allUserApps = allUserApps
      state.publisherAppMap = publisherAppMap
    },
    /**
     * Mutation to set user installed apps.
     * @param {Array} apps - The list of user installed apps.
     */
    setUserInstalledApps(state, apps) {
      state.userInstalledApps = apps
    },
    /**
     * Mutation to set site installed apps.
     * @param {Array} apps - The list of site installed apps.
     */
    setSiteInstalledApps(state, apps) {
      state.siteInstalledApps = apps
    },
    /**
     * Mutation to set site apps formatted for MoreAppBar.
     * @param {Array} apps - The list of site apps formatted for MoreAppBar.
     */
    setSiteAppsForMoreBar(state, apps) {
      state.siteAppsForMoreBar = apps
    },
    /**
     * Mutation to set SDK roles.
     * @param {Array} roles - The list of SDK roles.
     */
    setRoles(state, roles) {
      state.SDKRoles = roles
    },
    /**
     * Mutation to reset publisher state (used on logout).
     */
    reset(state) {
      state.userInstalledApps = []
      state.siteInstalledApps = []
      state.siteAppsForMoreBar = []
      state.allSiteApps = []
      state.allUserApps = []
      state.publisherAppMap = []
      state.SDKRoles = []
    },
  },
  actions: {
    /**
     * Action to load the list of publishers.
     */
    loadPublisherList({ commit }) {
      const publishersList = [
        {
          name: 'forge',
          id: 'forge',
          parse_server_url: EXTENSIONS_MARKETPLACE_PARSE_SERVER_URL,
          parse_server_app_id: EXTENSIONS_MARKETPLACE_PARSE_SERVER_APP_ID,
          parse_server_site_id: EXTENSIONS_MARKETPLACE_PARSE_SERVER_SITE_ID,
        },
      ]
      // Mock data for now.
      commit('setPublisherList', publishersList)
    },
    /**
     * Action to load all apps.
     */
    async loadAllApps({ commit, state, rootState }) {
      let allSiteApps = [],
        allUserApps = []
      let publisherAppMap = {}
      const email = rootState.user.current.email
      const promises = state.publishers.map(async (publisher) => {
        const { parse_server_url, parse_server_app_id, parse_server_site_id } =
          publisher
        initParse(parse_server_url, parse_server_app_id)
        const developer = await findDeveloperByEmail(
          parse_server_site_id,
          email
        )
        const { siteApps, userApps } =
          await getReadyForSalePlugins(parse_server_site_id)
        const [mySiteApps, myUserApps] = await getMyPluginsInDevelopment(
          parse_server_site_id,
          developer?.id
        )
        allSiteApps = [...allSiteApps, ...siteApps, ...mySiteApps]
        allUserApps = [...allUserApps, ...userApps, ...myUserApps]
        publisherAppMap[publisher.id] = [
          ...siteApps,
          ...userApps,
          ...mySiteApps,
          myUserApps,
        ]
      })
      await Promise.all(promises)
      commit('setAllApps', { allSiteApps, allUserApps, publisherAppMap })
    },
    /**
     * Action to load user installed apps.
     */
    async loadUserInstalledApps({ rootState, state, commit }) {
      const userId = rootState.user.current.id
      if (userId) {
        const promises = state.publishers.map(async (publisher) => {
          const {
            parse_server_url,
            parse_server_app_id,
            parse_server_site_id,
          } = publisher
          initParse(parse_server_url, parse_server_app_id)
          const apps = await getUserInstalledApps(parse_server_site_id, userId)
          return apps
        })

        const appsArray = await Promise.all(promises)
        const apps = appsArray.flat()
        commit('setUserInstalledApps', apps)
      }
    },
    /**
     * Action to load site installed apps.
     * @param {string} siteId - The ID of the site.
     */
    async loadSiteInstalledApps({ state, commit, rootState }, siteId) {
      try {
        // Use marketplace API to fetch installations
        const marketplaceAPI = (await import('@/services/marketplace')).default
        const installations = await marketplaceAPI.listInstallations({
          scope_type: 'site',
          scope_id: siteId.toString(),
          status: 'active',
        })
        
        // Map installations to legacy format
        // If plugin data is incomplete, fetch full plugin details
        const apps = await Promise.all(installations.map(async (installation) => {
          // Handle both old format and new format
          const installScopeType = installation.install_scope?.type || installation.install_scope_type
          const installScopeId = installation.install_scope?.id || installation.install_scope_id
          let plugin = installation.plugin || {}
          const version = installation.version || installation.plugin_version || {}
          
          // If plugin data is incomplete (missing entrypoint_url), fetch full plugin details
          let entrypointUrl = plugin.entrypoint_url || plugin.iframe_src || plugin.url || ''
          if (!entrypointUrl && plugin.slug) {
            try {
              const fullPluginResponse = await marketplaceAPI.getPlugin(plugin.slug)
              // Handle response format - could be direct plugin object or wrapped
              const fullPlugin = fullPluginResponse.plugin || fullPluginResponse.data || fullPluginResponse
              if (fullPlugin && (fullPlugin.entrypoint_url || fullPlugin.iframe_src || fullPlugin.url)) {
                plugin = { ...plugin, ...fullPlugin } // Merge with existing plugin data
                entrypointUrl = fullPlugin.entrypoint_url || fullPlugin.iframe_src || fullPlugin.url || ''
              }
            } catch (error) {
              console.warn(`Failed to fetch plugin details for ${plugin.slug}:`, error)
            }
          }
          
          return {
            id: installation.id,
            pluginId: installation.plugin_id || plugin.id,
            plugin: plugin,
            version: version,
            param: installation.config || {},
            installScopeType: installScopeType,
            installScopeId: installScopeId,
            status: installation.status,
            // Map to legacy format expected by components
            developerApp: {
              id: plugin.id,
              slug: plugin.slug, // This is the key field for matching
              name: plugin.name,
              url: entrypointUrl, // URL for SdkServer iframe
              entrypoint_url: entrypointUrl,
              installParams: plugin.install_params ? JSON.stringify(plugin.install_params) : '[]',
              sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
            },
            // Also include slug and URL at top level for easier access
            slug: plugin.slug,
            siteSrc: entrypointUrl, // For SdkServer fallback
            // Include sandboxPermissions at top level for SitePlugin.vue
            sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
          }
        }))
        
        commit('setSiteInstalledApps', apps)
        
        // Also populate state.sites.apps for MoreAppBar display
        // Convert installations to the format expected by MoreAppBar
        const siteApps = installations.map((installation) => {
          const plugin = installation.plugin || {}
          const entrypointUrl = plugin.entrypoint_url || plugin.iframe_src || plugin.url || ''
          
          return {
            slug: plugin.slug,
            extensionId: plugin.slug, // Use slug as extensionId
            label: plugin.name || 'Unnamed Plugin',
            icon: plugin.key_image_url || plugin.thumbnail_url || plugin.image_url || null,
            component: true, // Indicates this is a plugin component
            siteSrc: entrypointUrl,
            pluginId: plugin.id,
            param: installation.config || {},
            // Include sandboxPermissions from plugin (marketplace API uses sandbox_permissions)
            sandboxPermissions: plugin.sandbox_permissions || plugin.sandboxPermissions || [],
            // Store full installation data for reference
            installation: installation,
          }
        })
        
        // Update state.sites.apps with installed plugins for MoreAppBar
        // Access sites state from rootState
        const sitesState = rootState.sites || {}
        const existingApps = sitesState.apps || []
        const allApps = [...existingApps]
        
        // Add new plugins, avoiding duplicates by slug
        siteApps.forEach((newApp) => {
          const existingIndex = allApps.findIndex(
            (app) => app.slug === newApp.slug || app.extensionId === newApp.slug
          )
          if (existingIndex === -1) {
            allApps.push(newApp)
          } else {
            // Update existing app with installation data
            allApps[existingIndex] = { ...allApps[existingIndex], ...newApp }
          }
        })
        
        // Dispatch action to update sites.apps
        // We'll handle this in the component that calls this action
        commit('setSiteAppsForMoreBar', siteApps)
      } catch (error) {
        console.error('Error loading site installed apps:', error)
        // Fallback to old method if marketplace API fails
        const promises = state.publishers.map(async (publisher) => {
          const { parse_server_url, parse_server_app_id, parse_server_site_id } =
            publisher
          initParse(parse_server_url, parse_server_app_id)
          const apps = await getSiteInstalledApps(parse_server_site_id, siteId)
          return apps
        })

        const appsArray = await Promise.all(promises)
        const apps = appsArray.flat()
        commit('setSiteInstalledApps', apps)
      }
    },
    /**
     * Action to load roles.
     */
    async loadRoles({ state, commit }) {
      const promises = state.publishers.map(async (publisher) => {
        const { parse_server_url, parse_server_app_id, parse_server_site_id } =
          publisher
        initParse(parse_server_url, parse_server_app_id)
        const apps = await getRolesList(parse_server_site_id)
        return apps
      })
      const rolesArray = await Promise.all(promises)
      const roles = rolesArray.flat()
      commit('setRoles', roles)
    },
    /**
     * Action to load site information.
     */
    async loadSite({ rootState, commit }) {
      const publisher = rootState.sites.active?.publisherInfo
      commit('setActivePublisher', publisher)
    },
    /**
     * Action to load all necessary data.
     * @param {string} id - The ID of the site.
     */
    async load({ dispatch }, id) {
      dispatch('loadPublisherList')
      dispatch('loadSite')
      dispatch('loadAllApps')
      dispatch('loadUserInstalledApps')
      dispatch('loadSiteInstalledApps', id)
      dispatch('loadRoles')
    },
  },
  getters: {
    /**
     * Getter to find an app by its slug.
     * @param {String} slug - Target app slug
     * @returns {Object|null} The found app or null if not found.
     */
    findAppBySlug: (state) => (slug) => {
      return [...state.allSiteApps, ...state.allUserApps].find(
        (app) => app.slug == slug
      )
    },
    /**
     * Getter to find a publisher by its ID.
     * @param {String} id -  Target publisher id
     * @returns {Object|null} The found publisher or null if not found.
     */
    findPublisherById: (state) => (id) => {
      return state.publishers.find((publisher) => publisher.id == id)
    },
    /**
     * Getter to get the publisher by app ID.
     * @param {String} appId -  Target app id
     * @returns {Object|null} The found publisher by the given appId or null if not found.
     */
    getPublisherByAppId: (state, getters) => (appId) => {
      if (state.publisherAppMap) {
        const publisherId = Object.keys(state.publisherAppMap).find((id) => {
          const apps = state.publisherAppMap[id]
          const app = apps.find((ap) => ap.id == appId)
          return !!app
        })
        if (publisherId) return getters['findPublisherById'](publisherId)
      }
      return null
    },
  },
}

export default store
